import { createContext, useContext, useEffect, useState, ReactNode } from 'react'

type Theme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  toggleTheme: () => void
  mounted: boolean
}

const ThemeContext = createContext<ThemeContextType>({
  theme: 'dark',
  toggleTheme: () => {},
  mounted: false,
})

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>('dark')
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    
    setMounted(true)
    try {
      // First check localStorage for saved theme preference
      const savedTheme = localStorage.getItem('theme') as Theme
      
      // Then check what's already applied by the inline script
      const root = window.document.documentElement
      const currentTheme = root.classList.contains('dark') ? 'dark' : 
                           root.classList.contains('light') ? 'light' : null
      
      // Prioritize saved theme from localStorage, then current DOM theme, then system preference, then default
      if (savedTheme === 'light' || savedTheme === 'dark') {
        setTheme(savedTheme)
        // Ensure DOM matches saved theme
        if (currentTheme !== savedTheme) {
          root.classList.remove('light', 'dark')
          root.classList.add(savedTheme)
          if (savedTheme === 'dark') {
            root.style.backgroundColor = '#020617'
            document.body.style.backgroundColor = '#020617'
          } else {
            root.style.backgroundColor = '#ffffff'
            document.body.style.backgroundColor = '#ffffff'
          }
        }
      } else if (currentTheme && (currentTheme === 'light' || currentTheme === 'dark')) {
        setTheme(currentTheme)
        // Save current theme to localStorage
        localStorage.setItem('theme', currentTheme)
      } else {
        // Check system preference
        const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
        const defaultTheme = prefersDark ? 'dark' : 'light'
        setTheme(defaultTheme)
        root.classList.remove('light', 'dark')
        root.classList.add(defaultTheme)
        localStorage.setItem('theme', defaultTheme)
        if (defaultTheme === 'dark') {
          root.style.backgroundColor = '#020617'
          document.body.style.backgroundColor = '#020617'
        } else {
          root.style.backgroundColor = '#ffffff'
          document.body.style.backgroundColor = '#ffffff'
        }
      }
    } catch (error) {
      // localStorage might not be available, default to dark
      setTheme('dark')
      console.warn('Failed to load theme from localStorage:', error)
    }
  }, [])

  useEffect(() => {
    if (mounted && typeof window !== 'undefined') {
      const root = window.document.documentElement
      // Only update if theme actually changed to avoid unnecessary DOM manipulation
      const currentTheme = root.classList.contains('dark') ? 'dark' : 
                           root.classList.contains('light') ? 'light' : null
      
      if (currentTheme !== theme) {
        root.classList.remove('light', 'dark')
        root.classList.add(theme)
        // Update background color for smooth transition
        if (theme === 'dark') {
          root.style.backgroundColor = '#020617'
          document.body.style.backgroundColor = '#020617'
        } else {
          root.style.backgroundColor = '#ffffff'
          document.body.style.backgroundColor = '#ffffff'
        }
      }
      
      try {
        localStorage.setItem('theme', theme)
      } catch (error) {
        // localStorage might not be available in some contexts
        console.warn('Failed to save theme to localStorage:', error)
      }
    }
  }, [theme, mounted])

  const toggleTheme = () => {
    setTheme(prevTheme => prevTheme === 'light' ? 'dark' : 'light')
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, mounted }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  return context
}
